/**
 * @file src/offline/offlineUpdates.js
 * @description Downloading again from the library page: refreshing what is here, and taking a
 * newer release when one exists.
 *
 * The library page is the one surface that shows every download at once, so it is where "this one
 * is out of date" and "get it again" belong. What it deliberately does NOT do is browse: orbiters
 * has no endpoint that lists a person's collections — only `/collections/{id}/public` — so a
 * collection this browser has never downloaded cannot be discovered from here. Adding one starts
 * where collections are actually listed, and the page says so rather than offering a control that
 * would have nothing to put in it.
 *
 * ## An update is offered, never applied
 *
 * A newer release is a NEW key, and the whole store is built on the rule that bytes somebody asked
 * to keep are not swapped underneath them. So the check only ever WRITES A FLAG; taking the newer
 * version is a thing a person does, by pressing a button, having been told.
 *
 * ## Everything here needs a connection, and says so
 *
 * Checking and updating are the two things on that page which cannot work offline. They are gated
 * on `navigator.onLine` — which is a weak signal, but the right one HERE: it answers "is there a
 * link", and its false positives (a captive portal, a dead backend) fail as a normal request
 * failure, which these already handle. Its false negatives simply mean the button waits.
 */
import { downloadCollection } from './offlineLibrary.js';
import { unpinCacheEntries } from '../audio/playback/audioCacheStore.js';
import {
  DOWNLOAD_STATE,
  listDownloadedCollections,
  patchDownloadedCollection,
  readDownloadedCollection,
  writeDownloadedCollection,
} from './offlineLibraryStore.js';

/** Whether the two online-only actions can be attempted at all. */
export function canReachNetwork() {
  return typeof navigator === 'undefined' || navigator.onLine !== false;
}

/** Stable roster identity across all three entity kinds. Entity type wins over incidental ids. */
function rosterEntryKey(entry) {
  const type = String(entry?.entityType || '').toLowerCase();
  if (type === 'world' || type === 'entangled-world') {
    return entry?.entangledWorldId ? `world:${entry.entangledWorldId}` : null;
  }
  if (type === 'orbiter') return entry?.orbiterId ? `orbiter:${entry.orbiterId}` : null;
  if (type === 'track') return entry?.trackId ? `track:${entry.trackId}` : null;
  if (entry?.trackId) return `track:${entry.trackId}`;
  if (entry?.orbiterId) return `orbiter:${entry.orbiterId}`;
  if (entry?.entangledWorldId) return `world:${entry.entangledWorldId}`;
  return null;
}

/**
 * Ask, for every downloaded collection, whether a newer release exists.
 *
 * One collection failing is not the others' problem — an archived collection answers 404 and its
 * neighbours are still worth checking — so each is caught on its own.
 *
 * ## Why this asks about TRACKS rather than collections
 *
 * The collection endpoint cannot answer it. `/collections/{id}/public?hydrationLevel=1` is cards
 * only — no release, no version anywhere in the payload — and the level-2 hydration that does carry
 * one is measured in seconds PER ITEM, which is not a thing to spend on a background check. So the
 * question is asked of each distinct track's own release instead, which is the cheapest place the
 * answer actually exists.
 *
 * It is still N requests, so this runs only when somebody ASKS — never on opening the page.
 *
 * @param {{ fetchTrackVersion: (trackId: string) => Promise<number|null> }} deps resolves a track's
 *   LIVE release version. Injected so this module does not pull the API client into a bundle that
 *   may not need it.
 * @returns {Promise<number>} how many are now marked as having an update.
 */
export async function checkForCollectionUpdates({ fetchTrackVersion }) {
  if (!canReachNetwork()) return 0;

  const manifests = await listDownloadedCollections();

  // One request per DISTINCT track across the whole library. Ten collections that share a track ask
  // about it once, which matters because this is the expensive part.
  const wanted = new Set();
  for (const manifest of manifests) {
    for (const entry of manifest.entries || []) if (entry?.trackId) wanted.add(entry.trackId);
  }

  const liveVersions = new Map();
  for (const trackId of wanted) {
    try {
      const version = await fetchTrackVersion(trackId);
      if (version !== null && version !== undefined) liveVersions.set(trackId, version);
    } catch {
      // Unreachable, archived, or no longer visible to this person. Not knowing is not a reason to
      // say anything about the download — it stays exactly as it is.
    }
  }

  let flagged = 0;
  for (const manifest of manifests) {
    const available = (manifest.entries || []).some((entry) => {
      const live = liveVersions.get(entry.trackId);
      if (live === undefined) return false;
      return Number(live) > Number(entry.trackVersion);
    });
    // Written even when it turns FALSE: a collection whose newer release was taken, or rolled back,
    // must stop claiming an update. A flag that only ever goes on is a flag nobody trusts.
    if (manifest.updateAvailable !== available) {
      await patchDownloadedCollection(manifest.collectionId, { updateAvailable: available });
    }
    if (available) flagged += 1;
  }
  return flagged;
}

/**
 * Ask, for every downloaded collection, whether its ROSTER has changed.
 *
 * The version check above cannot see this, and that is not a detail: it asks about the tracks the
 * manifest already names, so a track ADDED to a collection after it was downloaded is a track
 * nothing ever asks about. The download went on reporting itself up to date, and the only way to get
 * the new audio was to remove the collection and download it again — which is what people did,
 * because nothing told them there was anything to do.
 *
 * This is the CHEAP check, and the cheaper of the two by a distance: one request per collection
 * against `hydrationLevel=1`, which is cards only, versus one request per distinct TRACK for the
 * version check. So this one can run when the library page opens, and the expensive one stays a
 * thing somebody asks for.
 *
 * A collection that lost tracks counts too. Its download still holds bytes for entries the roster no
 * longer has, and updating is what releases them.
 *
 * @param {{ fetchCollection: (id: string) => Promise<object> }} deps
 * @returns {Promise<number>} how many collections have a roster change waiting.
 */
export async function checkForRosterChanges({ fetchCollection }) {
  if (!canReachNetwork()) return 0;

  const manifests = await listDownloadedCollections();
  let flagged = 0;

  for (const manifest of manifests) {
    let live;
    try {
      live = await fetchCollection(manifest.collectionId);
    } catch {
      // Archived, unreachable, or no longer visible to this person. Not knowing is not a reason to
      // say anything about the download — it stays exactly as it is.
      continue;
    }

    const liveIds = new Set((live?.roster || []).map(rosterEntryKey).filter(Boolean));
    const storedIds = new Set((manifest.entries || []).map(rosterEntryKey).filter(Boolean));
    // Items the roster has and the download does not. Counted rather than merely detected, because
    // "3 new items" is a different sentence from "newer versions" and the row says which it is.
    const added = [...liveIds].filter((id) => !storedIds.has(id));
    const removed = [...storedIds].filter((id) => !liveIds.has(id));

    // A track the download SKIPPED is not a track the roster added — it was there and it could not
    // be stored (a draft, or a card that is not a recording). Counting it would leave a collection
    // permanently claiming an update that updating cannot resolve.
    const skipped = new Set((manifest.skippedTrackIds || []).map(String));
    const genuinelyAdded = added.filter((key) => !skipped.has(key.split(':').slice(1).join(':')));

    const changed = genuinelyAdded.length > 0 || removed.length > 0;
    const patch = {
      addedTrackCount: genuinelyAdded.length,
      removedTrackCount: removed.length,
    };
    // Written even when it turns FALSE, for the same reason the version flag is: a collection whose
    // change was taken must stop claiming one.
    if (
      manifest.addedTrackCount !== patch.addedTrackCount
      || manifest.removedTrackCount !== patch.removedTrackCount
    ) {
      await patchDownloadedCollection(manifest.collectionId, patch);
    }
    if (changed) flagged += 1;
  }

  return flagged;
}

/**
 * Download a collection again at its CURRENT releases.
 *
 * Deliberately the same call the collection drawer makes, with a freshly fetched roster: the
 * download path already knows how to re-download over an existing entry, inheriting its pins so
 * shared bytes are not taken twice and the cap is not paid twice. Re-implementing "update" as
 * anything other than "download it again" would be a second code path for the one operation that
 * must not get the refcounting wrong.
 *
 * @param {string} collectionId
 * @param {{ fetchCollection: (id: string) => Promise<object> }} deps
 * @returns {Promise<object>} the stored manifest
 */
export async function updateDownloadedCollection(collectionId, { fetchCollection, quality = null }) {
  const live = await fetchCollection(collectionId);
  // The address this entry already reopens at. `downloadCollection` otherwise rebuilds it from
  // `window.location`, and on the library page that carries none of the display choices the
  // original download preserved — so updating a collection would silently drop the graphics tier
  // and language it was kept with.
  const existing = await readDownloadedCollection(collectionId).catch(() => null);
  // The copy that currently plays. An update REPLACES the manifest before it has proven it can
  // fetch the new release's bytes, so a download that then fails leaves a partial new manifest
  // where a complete old one used to be — the collection stops playing offline, and its old bytes
  // are still on disk, simply no longer named by anything.
  const wasReady = existing?.state === DOWNLOAD_STATE.READY;
  let stored;
  try {
    stored = await downloadCollection({
      collectionId,
      name: live?.name ?? null,
      coverImage: live?.coverImage ?? null,
      layout: live?.layout ?? existing?.layout ?? null,
      sourceUrl: existing?.entryUrl ?? null,
      // The manifest resolves tracks, orbiters and worlds. Passing only tracks here made Update
      // erase the non-track rows and release their stored payloads/textures.
      roster: live?.roster || [],
    },
    // Null means "whatever this collection already is", which is what an update wants: taking a
    // newer release must not quietly return a lossless collection to compressed. A switch is the
    // one caller that names a quality, because changing it is the whole point of that call.
    { quality });
  } catch (error) {
    // The download THREW rather than returning something partial — an abort, a cap refusal, a
    // manifest that would not build. Each of those also leaves the new incomplete manifest where
    // the old complete one was, so the restore belongs here too; handling only the returning
    // failure made "a failed update puts the working copy back" true for one case in four.
    await restoreWorkingCopy(collectionId, existing, wasReady);
    throw error;
  }

  if (wasReady && stored?.state !== DOWNLOAD_STATE.READY) {
    await restoreWorkingCopy(collectionId, existing, wasReady);
    return existing;
  }

  // It is now at the versions AND the roster the server just described, so it is behind neither.
  await patchDownloadedCollection(collectionId, {
    updateAvailable: false,
    addedTrackCount: 0,
    removedTrackCount: 0,
  });
  return stored;
}

/**
 * Put a working download back after an update failed, without leaking the failed run's pins.
 *
 * Restoring the old manifest verbatim records the OLD `pinnedKeys`, while the abandoned run has
 * already pinned whatever new-release bytes it managed to fetch. Those keys would then be named by
 * nothing: unreachable, uncounted by `bytes`, and un-evictable, because a pin is exactly what the
 * cap refuses to evict. So the extra pins are released first, and only then is the old manifest
 * written back.
 */
async function restoreWorkingCopy(collectionId, existing, wasReady) {
  if (!wasReady || !existing) return;

  const failed = await readDownloadedCollection(collectionId).catch(() => null);
  const keep = new Set(existing.pinnedKeys || []);
  const orphaned = (failed?.pinnedKeys || []).filter((key) => !keep.has(key));
  if (orphaned.length) await unpinCacheEntries(orphaned).catch(() => {});

  // The flag stays ON: the newer release still exists, and this can be tried again.
  await writeDownloadedCollection({ ...existing, updateAvailable: true });
}
